import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import '../styles/pages/DeleteAccountPage.css';

const DeleteAccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = '退会手続き | はしもと栄養パートナー';
  }, []);
  const [formData, setFormData] = useState({
    password: '',
    confirmDeletion: '',
    reason: '',
    feedback: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: confirmation, 2: form, 3: success

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'パスワードを入力してください';
    }
    
    if (formData.confirmDeletion !== 'DELETE') {
      newErrors.confirmDeletion = '「DELETE」と正確に入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.delete('/api/auth/account', {
        data: {
          password: formData.password,
          confirmDeletion: formData.confirmDeletion,
          reason: formData.reason,
          feedback: formData.feedback
        }
      });

      setStep(3);
      
      // Logout and redirect after 5 seconds
      setTimeout(() => {
        logout();
        navigate('/');
      }, 5000);

    } catch (error) {
      setErrors({
        submit: error.response?.data?.message || 'アカウント削除に失敗しました'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (step === 1) {
    return (
      <AppLayout hideTabNav>
        <PageTitle title="アカウントの削除" subtitle="この操作は取り消すことができません" icon="⚠️" />
        <div className="tsumugi-container py-6">
          <div className="max-w-2xl mx-auto">
            <div className="tsumugi-card space-y-6">
              <p className="text-sm delete-account-page__hint">
                削除前に以下の内容をご確認ください。
              </p>

              <div className="delete-account-page__warning-box">
                <h4 className="delete-account-page__warning-title">削除される情報:</h4>
                <ul className="delete-account-page__warning-list space-y-1">
                  <li>• アカウント情報（{user.email}）</li>
                  <li>• プロフィール情報</li>
                  <li>• 相談履歴・アドバイス履歴</li>
                  <li>• 使用統計データ</li>
                  {user.plan !== 'free' && <li>• サブスクリプション（自動解約されます）</li>}
                </ul>
              </div>

              <div className="delete-account-page__suggestion-box">
                <h4 className="delete-account-page__suggestion-title">削除前にお試しください:</h4>
                <ul className="delete-account-page__suggestion-list space-y-1">
                  <li>• アカウントを一時的に無効にする</li>
                  <li>• プランをフリープランにダウングレードする</li>
                  <li>• しばらく利用を控える</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="tsumugi-btn-primary delete-account-page__delete-btn"
                >
                  削除手続きを進める
                </button>
                <button
                  onClick={() => navigate('/mypage')}
                  className="tsumugi-btn-secondary"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (step === 2) {
    return (
      <AppLayout hideTabNav>
        <PageTitle title="アカウント削除の確認" subtitle="最終確認を行ってください" icon="🔐" />
        <div className="tsumugi-container py-6">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit}>
              <div className="tsumugi-card space-y-6">
                {errors.submit && (
                  <div className="delete-account-page__confirmation-box">
                    {errors.submit}
                  </div>
                )}

                <div>
                  <label htmlFor="reason" className="block text-sm font-medium mb-2 delete-account-page__label">
                    削除理由（任意）
                  </label>
                  <select
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    className="delete-account-page__input w-full px-4 py-3"
                  >
                    <option value="">選択してください</option>
                    <option value="not_useful">サービスが役に立たない</option>
                    <option value="too_expensive">料金が高い</option>
                    <option value="found_alternative">他のサービスを見つけた</option>
                    <option value="privacy_concern">プライバシーの懸念</option>
                    <option value="temporary_break">一時的な利用停止</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="feedback" className="block text-sm font-medium mb-2 delete-account-page__label">
                    改善へのご意見（任意）
                  </label>
                  <textarea
                    id="feedback"
                    name="feedback"
                    rows={3}
                    value={formData.feedback}
                    onChange={handleChange}
                    placeholder="サービス改善のために、ご意見をお聞かせください"
                    className="delete-account-page__textarea"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2 delete-account-page__label">
                    パスワード確認 <span className="delete-account-page__required">*</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="現在のパスワードを入力"
                    className="delete-account-page__input w-full px-4 py-3"
                    required
                  />
                  {errors.password && (
                    <p className="delete-account-page__error">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmDeletion" className="block text-sm font-medium mb-2 delete-account-page__label">
                    削除確認 <span className="delete-account-page__required">*</span>
                  </label>
                  <p className="delete-account-page__instruction">
                    アカウント削除を実行するには、下のフィールドに「<strong>DELETE</strong>」と入力してください。
                  </p>
                  <input
                    type="text"
                    id="confirmDeletion"
                    name="confirmDeletion"
                    value={formData.confirmDeletion}
                    onChange={handleChange}
                    placeholder="DELETE"
                    className="delete-account-page__input w-full px-4 py-3"
                    required
                  />
                  {errors.confirmDeletion && (
                    <p className="delete-account-page__error">{errors.confirmDeletion}</p>
                  )}
                </div>

                <div className="pt-5">
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="tsumugi-btn-secondary"
                    >
                      戻る
                    </button>
                    <button
                      type="submit"
                      disabled={loading || formData.confirmDeletion !== 'DELETE'}
                      className="tsumugi-btn-primary delete-account-page__delete-btn disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin -ml-1 mr-3 h-5 w-5">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                          削除中...
                        </div>
                      ) : (
                        'アカウントを削除する'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (step === 3) {
    return (
      <AppLayout hideTabNav>
        <PageTitle title="アカウント削除完了" subtitle="ご利用ありがとうございました" icon="✅" />
        <div className="tsumugi-container py-6">
          <div className="max-w-md mx-auto">
            <div className="tsumugi-card text-center">
              <div className="delete-account-page__success-icon-wrapper">
                <svg className="delete-account-page__success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="delete-account-page__success-title">
                アカウント削除完了
              </h2>
              <div className="delete-account-page__success-box">
                <p className="delete-account-page__success-message">
                  アカウントが正常に削除されました。<br />
                  長い間ご利用いただき、ありがとうございました。
                </p>
              </div>
              <p className="delete-account-page__success-note">
                5秒後にホームページに移動します。
              </p>
              <p className="delete-account-page__success-footer">
                削除完了のお知らせメールをお送りしました。
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return null;
};

export default DeleteAccountPage;